import { css } from "@emotion/react";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CodeBlack, ContentCopy, Info, OpenInNew } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  IPickerListItem,
  Icon,
  Link,
  Picker,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import { AccordionCard } from "components/accordion-card/AccordionCard";
import { CodeSnippet } from "components/code-snippet/CodeSnippet";
import { CopyToClipboard } from "components/copy-to-clipboard-input";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { HelpCenter } from "constants/help-center";
import { OpenWidget } from "constants/settings/openWidget";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useSetOpenWidgetTeam } from "hooks/settings/openwidget/use-set-openwidget-team";
import { useAgentFlag } from "hooks/use-agent-flag";
import { selectIsAdmin, selectIsViewer } from "store/entities/agents";
import {
  selectIsOpenWidgetTeamValid,
  selectOpenWidget,
  selectOpenWidgetExternalId,
  selectOpenWidgetId,
} from "store/entities/integrations";
import { ITeam, selectTeams, selectTeamsAlphabetically } from "store/entities/teams";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { withVar } from "themes/helpers";

import { SettingsHeader } from "../styled";

import ContentWrapper from "./components/ContentWrapper";
import { InstallationStatus } from "./components/InstallationStatus";
import { NoSelectedTeamAlert } from "./components/NoSelectedTeamAlert";
import { NonAdminAlert } from "./components/NonAdminAlert";
import { trackLearnMoreClicked } from "./helpers";
import { iconInfoCss } from "./styles";

interface IProps {
  snippetCode?: string;
  installed: boolean;
}

const getTeamPickerListItem = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    listItemBody: <TeamPickerListItem name={team.name} pickerType="single" isDefault={team.isDefault} />,
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="single" isDefault={team.isDefault} />,
  },
});

export const Connected: FC<IProps> = ({ snippetCode, installed }) => {
  const dispatch = useDispatch();
  const integration = useSelector(selectOpenWidget);
  const teams = useSelector(selectTeams);
  const teamsAlphabetically = useSelector(selectTeamsAlphabetically);
  const isAdmin = useSelector(selectIsAdmin);
  const isViewer = useSelector(selectIsViewer);
  const externalId = useSelector(selectOpenWidgetExternalId);
  const [snippetCollapsed, setSnippetCollapsed] = useAgentFlag("openWidgetSnippetCollapsed");

  const integrationId = useSelector(selectOpenWidgetId);
  const currentTeamId = integration?.settings?.teamID || null;
  const [currentTeamIdOptimistic, setCurrentTeamOptimistic] = useState<string | null>(currentTeamId);
  const { mutate: setTeam } = useSetOpenWidgetTeam({ onError: () => setCurrentTeamOptimistic(currentTeamId) });
  const isTeamValid = useSelector(selectIsOpenWidgetTeamValid);

  const onTeamSelect = (selected: IPickerListItem[] | null): void => {
    if (!selected || selected.length === 0 || selected[0].key === currentTeamIdOptimistic) {
      return;
    }
    const selectedId = selected[0].key;
    setCurrentTeamOptimistic(selectedId);
    setTeam({ teamId: selectedId, integrationId });
    trackEngagement("OpenWidget: Changed team", EventPlace.Settings, { from: currentTeamId, to: selectedId });
  };

  const onCodeCopy = (): void => {
    trackEngagement("OpenWidget: Copied installation code", EventPlace.Settings);
    dispatch(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.OPENWIDGET_COPY_CODE_SUCCESS),
      })
    );
  };

  const onCustomizeWidget = (): void => {
    trackEngagement("OpenWidget: 'Customize widget' clicked", EventPlace.Settings);
  };

  const onPreviewTheWidget = (): void => {
    trackEngagement("OpenWidget: 'Preview the widget' clicked", EventPlace.Settings);
  };

  return (
    <ContentWrapper>
      <Text>
        Install a contact widget on your website to let visitors leave contact requests, suggestions, and feedback using
        well-known forms. Customize it with your avatar, name, and welcome message.{" "}
        <Link
          bold={false}
          href={HelpCenter.INTEGRATE_HELPDESK_WITH_OPENWIDGET}
          target="_blank"
          onClick={() => trackLearnMoreClicked("connected")}
        >
          Learn more
        </Link>
      </Text>
      {!isAdmin && <NonAdminAlert />}
      {isAdmin && !isTeamValid && <NoSelectedTeamAlert />}
      <Flex column maxWidth="400px">
        <FormField labelText="Assign tickets from the widget to a team">
          <Picker
            isRequired
            disabled={!isAdmin}
            id="open-widget-team-select"
            data-testid="open-widget-team-select"
            options={teamsAlphabetically.map(getTeamPickerListItem)}
            selected={isTeamValid ? [getTeamPickerListItem(teams[currentTeamId])] : []}
            onSelect={onTeamSelect}
          />
        </FormField>
      </Flex>
      <AccordionCard
        title="Install widget"
        onToggle={isOpen => setSnippetCollapsed(!isOpen)}
        openByDefault={!snippetCollapsed}
        icon={<Icon source={CodeBlack} customColor={withVar(DesignToken.ContentBasicPrimary)} />}
        rightNode={<InstallationStatus installed={installed} />}
      >
        <Flex column gap="16px">
          <Text>
            Copy the code and paste it before the closing <b>{"</body>"}</b> tag to install the widget on your website.
          </Text>
          <Block>
            <CopyToClipboard value={snippetCode} onCopiedHandler={onCodeCopy}>
              {({ handleCopy }) => (
                <Button
                  icon={<Icon source={ContentCopy} />}
                  kind="primary"
                  onClick={handleCopy}
                  data-testid="copy-code"
                >
                  Copy code
                </Button>
              )}
            </CopyToClipboard>
          </Block>
          <CodeSnippet>{snippetCode}</CodeSnippet>
          {!isViewer && externalId !== null && (
            <Flex>
              <Icon source={Info} css={iconInfoCss} />
              <Block>
                <Text as="span">
                  <Text as="span" bold>
                    See what the widget will look like
                  </Text>{" "}
                  <Text as="span">
                    and learn about the other website installation options.{" "}
                    <Link
                      bold={false}
                      href={`${OpenWidget.Preview}${externalId}`}
                      target="_blank"
                      onClick={onPreviewTheWidget}
                    >
                      Preview the widget
                    </Link>
                  </Text>
                </Text>
              </Block>
            </Flex>
          )}
        </Flex>
      </AccordionCard>
      <Flex column gap={withVar(SpacingToken.Spacing2)}>
        <Block>
          <SettingsHeader>Customize widget</SettingsHeader>
          <Text css={css({ marginTop: "-0.5em" })}>
            Access contact widget settings to decide what goals the widget should fulfill and adjust its look.
          </Text>
        </Block>
        <Block>
          <Button
            kind="secondary"
            icon={<Icon source={OpenInNew} />}
            iconPosition="right"
            href={OpenWidget.Configurator}
            onClick={onCustomizeWidget}
            target="_blank"
            data-testid="customize-widget-button"
          >
            Customize widget
          </Button>
        </Block>
      </Flex>
    </ContentWrapper>
  );
};
